<script lang="ts">
    import { onMount } from 'svelte/internal'
    import dayjs from 'dayjs'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import DateSelect from '../_common/date-select.svelte'
    import Popover from '../_common/popover.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zgrid from '../_common/z/zgrid.svelte'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import { faUser, faEdit } from '@fortawesome/free-solid-svg-icons'
    import type { ChargeRefundData, Log } from '../../modules/paymentsApi/logs/log.entity'
    import { PaymentMethodsFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import PaymentsPurchaseShow from './payments-purchase-show.svelte'

    let show = false
    let entities: Log[] = []

    let dates: { from: Date; to: Date } = window.api.utils.dates.today()
    let dateSelect

    onMount(async () => {
        await loadEntities()
    })

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    async function loadEntities() {
        try {
            show = true
            let from = dates?.from || undefined
            const to = dates?.to || undefined

            // mesma regra da tela de parcelas: sem permissão de navegar, só os últimos 7 dias
            if (!window.store.token.permissions.pay.entries.navigate) {
                const diff = dayjs(new Date()).diff(from, 'days')
                if (!from || diff > 7) {
                    from = dayjs(new Date()).add(-7, 'day').toDate()
                    dateSelect?.setDate(from, to)
                }
            }

            entities = await window.api.payments.logs.getRefunds(from, to)

            // os nomes vêm do events-api em blocos: a lista pode ter centenas de linhas e uma
            // chamada por pessoa derrubaria a tela.
            const personsId = entities.map((el) => el.charge?.purchase?.referenceId).filter((el) => !!el)
            if (personsId.length) {
                const promises = []
                for (let i = 0; i < personsId.length; i += 100) {
                    const chunk = personsId.slice(i, i + 100)
                    promises.push(window.api.events.persons.getAll({ persons: chunk.join(','), removed: true }))
                }
                const results = await Promise.all(promises)
                let persons: Person[] = []
                for (const result of results) persons = persons.concat(result)
                for (const item of entities) {
                    item.person = persons.find((el) => el.personId === item.charge?.purchase?.referenceId)
                }
            }

            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function refundData(item: Log): ChargeRefundData {
        return (item.data || {}) as ChargeRefundData
    }

    // 'refunded' devolveu dinheiro de uma parcela paga; 'canceled' invalidou uma que nunca foi paga.
    // São coisas diferentes para a conciliação, então nunca aparecem sob o mesmo rótulo.
    function outcomeLabel(item: Log) {
        return refundData(item).outcome === 'refunded' ? 'Estorno' : 'Cancelamento'
    }

    function outcomeClass(item: Log) {
        return refundData(item).outcome === 'refunded' ? GridBadgeClassEnum.badge_alert : GridBadgeClassEnum.badge_default
    }

    function scopeLabel(item: Log) {
        return refundData(item).full ? 'Integral' : 'Parcial'
    }

    function instalment(item: Log) {
        if (!item.charge?.installmentNumber) return '-'
        if (!item.charge?.installmentTotal) return item.charge.installmentNumber.toString()
        return item.charge.installmentNumber + '/' + item.charge.installmentTotal
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        grid = new GridClass()
        grid.title = 'Estornos'
        grid.subtitle = window.store.token.permissions.pay.entries.navigate ? '' : 'Estornos dos últimos 7 dias.'
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime, size: 2 }))
        grid.cols.push(new GridCol({ content: 'Tipo', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Abrangência', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Nome', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Parcela', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Meio', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Valor da parcela', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: 'Estornado', type: GridColTypeEnum.float_2, showTotal: true, showTotalBox: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))

        for (const item of entities) {
            const data = refundData(item)
            const btnPerson = new GridButton({
                callback: () => window.person('show', item.charge?.purchase?.referenceId),
                icon: faUser,
                label: '',
            })
            const btnOpenPurchase = new GridButton({
                callback: () => showPurchaseDetail(item.purchaseId),
                icon: faEdit,
                label: 'Cobrança',
            })

            const line: GridLine = new GridLine()
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: new GridBadge({ label: outcomeLabel(item), class: outcomeClass(item) }) })
            line.slots.push({ content: scopeLabel(item) })
            line.slots.push({ content: item.person?.name || '[cliente não informado]' })
            line.slots.push({ content: item.charge?.purchase?.purchaseDescription || '-' })
            line.slots.push({ content: instalment(item) })
            line.slots.push({ content: PaymentMethodsFriendly[data.paymentMethod] || data.paymentMethod || '-' })
            line.slots.push({ content: item.charge?.value })
            line.slots.push({ content: data.amount })
            line.slots.push({ content: [btnPerson, btnOpenPurchase] })
            grid.lines.push(line)
        }
    }

    let purchaseId = null
    function showPurchaseDetail(id: string) {
        purchaseId = id
    }

    function closePurchaseShow() {
        purchaseId = null
        loadEntities()
    }
</script>

{#if purchaseId}
    <Popover title="Cobrança" width="1100px" height="80vh" on:close={closePurchaseShow}>
        <PaymentsPurchaseShow {purchaseId} on:close={closePurchaseShow} />
    </Popover>
{/if}

<Content footer={true}>
    <Zact>
        <DateSelect bind:this={dateSelect} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer h-mobile" />
    </Zact>
    <Zgrid class="h-space" {grid} />
    <LoadSpinner {show} />
</Content>
